import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { ContentSeries } from '@/client/courses';
import { PlannerCoursesFiltersType } from '@/client/planner/types';
import { Tag } from '@/client/tags';
import { availableLanguages } from '@/common/constants/languages';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { t } from 'i18next';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { MultiSelect } from 'primereact/multiselect';
import React, {
  SyntheticEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { TagsMultiselectInput } from '../form/selectors/TagsMultiselectInput';

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  .p-input-icon-left {
    position: absolute;
    left: var(--small-padding);
    color: var(--gray-darker);
    z-index: 1;
  }

  input {
    width: 100%;
    padding-left: calc(var(--medium-padding) + var(--small-padding));
  }
`;

type PlannerCoursesFiltersProps = {
  filters?: PlannerCoursesFiltersType;
  onFilterChange?: (filters: PlannerCoursesFiltersType) => void;
};

export const PlannerCoursesFilters: React.FC<PlannerCoursesFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [selectedTags, setSelectedTags] = useState<Tag[]>(filters?.tags || []);
  const [selectedSeries, setSelectedSeries] = useState<
    (ContentSeries | null)[]
  >(filters?.series || []);
  const [selectedCourseLanguages, setSelectedCourseLanguages] = useState<
    LanguagesEnum[]
  >(filters?.courseLanguages || []);
  const [searchValue, setSearchValue] = useState(filters?.search || '');
  const [isTagsFilterActive, setIsTagsFilterActive] = useState(
    !!filters?.tags && filters.tags.length > 0,
  );
  const [isSeriesFilterActive, setIsSeriesFilterActive] = useState(
    !!filters?.series && filters.series.length > 0,
  );
  const [isCourseLanguagesFilterActive, setIsCourseLanguagesFilterActive] =
    useState(!!filters?.courseLanguages && filters.courseLanguages.length > 0);
  const menuRef = useRef<Menu>(null);
  const isInternalUpdateRef = useRef(false);
  const prevFiltersRef = useRef(filters);

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      prevFiltersRef.current = filters;
      return;
    }

    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    if (!filtersChanged) {
      return;
    }

    prevFiltersRef.current = filters;

    if (!filters || Object.keys(filters).length === 0) {
      setSelectedTags([]);
      setSelectedSeries([]);
      setSelectedCourseLanguages([]);
      setSearchValue('');
      setIsTagsFilterActive(false);
      setIsSeriesFilterActive(false);
      setIsCourseLanguagesFilterActive(false);
    } else {
      if (filters.tags !== undefined) {
        setSelectedTags(filters.tags);
        setIsTagsFilterActive(filters.tags.length > 0);
      }
      if (filters.series !== undefined) {
        setSelectedSeries(filters.series);
        setIsSeriesFilterActive(filters.series.length > 0);
      }
      if (filters.courseLanguages !== undefined) {
        setSelectedCourseLanguages(filters.courseLanguages);
        setIsCourseLanguagesFilterActive(filters.courseLanguages.length > 0);
      }
      if (filters.search !== undefined) {
        setSearchValue(filters.search);
      }
    }
  }, [filters]);

  const seriesOptions: Array<{
    label: string;
    value: ContentSeries | null;
  }> = [
    ...Object.values(ContentSeries).map((series) => ({
      label: t(`content.series.${series}`),
      value: series as ContentSeries,
    })),
    {
      label: '',
      value: null,
    },
  ];

  const courseLanguagesOptions: Array<{
    label: string;
    value: LanguagesEnum;
  }> = useMemo(
    () =>
      availableLanguages.map((language) => ({
        label: languageDisplayLabel[language],
        value: language,
      })),
    [],
  );

  const filterMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('filter.tags'),
        disabled: isTagsFilterActive,
        command: () => setIsTagsFilterActive(true),
      },
      {
        label: t('filter.series'),
        disabled: isSeriesFilterActive,
        command: () => setIsSeriesFilterActive(true),
      },
      {
        label: t('generic.lang'),
        disabled: isCourseLanguagesFilterActive,
        command: () => setIsCourseLanguagesFilterActive(true),
      },
    ],
    [
      isTagsFilterActive,
      isSeriesFilterActive,
      isCourseLanguagesFilterActive,
      t,
    ],
  );

  useEffect(() => {
    if (onFilterChange) {
      isInternalUpdateRef.current = true;
      onFilterChange({
        tags: isTagsFilterActive ? selectedTags : undefined,
        series: isSeriesFilterActive ? selectedSeries : undefined,
        courseLanguages: isCourseLanguagesFilterActive
          ? selectedCourseLanguages
          : undefined,
        search: searchValue || undefined,
      });
    }
  }, [
    selectedTags,
    selectedSeries,
    selectedCourseLanguages,
    searchValue,
    isTagsFilterActive,
    isSeriesFilterActive,
    isCourseLanguagesFilterActive,
    onFilterChange,
  ]);

  const handleRemoveTagsFilter = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsTagsFilterActive(false);
    setSelectedTags([]);
  };

  const handleRemoveSeriesFilter = (e: SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setIsSeriesFilterActive(false);
    setSelectedSeries([]);
  };

  const handleRemoveCourseLanguagesFilter = (
    e: SyntheticEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setIsCourseLanguagesFilterActive(false);
    setSelectedCourseLanguages([]);
  };

  const handleTagsChange = (value: Tag[]) => {
    setSelectedTags(value);
  };

  const handleSeriesChange = (value: (ContentSeries | null)[]) => {
    setSelectedSeries(value);
  };

  const handleCourseLanguagesChange = (value: LanguagesEnum[]) => {
    setSelectedCourseLanguages(value);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  return (
    <FlexContainer direction="column" align="flex-start" gap={12}>
      <FlexContainer width="100%" gap={12} justify="flex-start">
        <FlexContainer wrap="wrap" width="auto" gap={12} justify="flex-start">
          <Menu model={filterMenuItems} popup ref={menuRef} />
          <AppButton
            className="w-max"
            label={t('filter.add')}
            icon="pi pi-filter"
            type="outlined"
            onClick={(event: SyntheticEvent<HTMLButtonElement>) =>
              menuRef.current?.toggle(event)
            }
            isDisabled={
              isTagsFilterActive &&
              isSeriesFilterActive &&
              isCourseLanguagesFilterActive
            }
          />
        </FlexContainer>
        <SearchInputWrapper className="w-full">
          <i className="pi pi-search p-input-icon-left" />
          <InputText
            value={searchValue}
            className="w-full"
            onChange={(e) => {
              handleSearchChange(e.target.value);
            }}
            placeholder="Search by name"
          />
        </SearchInputWrapper>
      </FlexContainer>
      {(isTagsFilterActive ||
        isSeriesFilterActive ||
        isCourseLanguagesFilterActive) && (
        <FlexContainer justify="flex-start" wrap="wrap" gap={12}>
          {isTagsFilterActive && (
            <FlexContainer width="auto">
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">{t('filter.tags')}</span>
                <TagsMultiselectInput
                  selectedOptions={selectedTags}
                  onChange={handleTagsChange}
                />
                <AppButton
                  className="group-button"
                  icon="pi pi-times"
                  severity="secondary"
                  type="text"
                  onClick={handleRemoveTagsFilter}
                />
              </div>
            </FlexContainer>
          )}
          {isSeriesFilterActive && (
            <FlexContainer width="auto">
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">{t('filter.series')}</span>
                <MultiSelect
                  value={selectedSeries}
                  onChange={(e) => {
                    handleSeriesChange(e.value || []);
                    e.stopPropagation();
                  }}
                  options={seriesOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={t('filter.series')}
                  maxSelectedLabels={0}
                  selectedItemsLabel={t('generic.itemsSelected.count')}
                  {...preventDropdownBrowserTranslate}
                />
                <AppButton
                  className="group-button"
                  icon="pi pi-times"
                  severity="secondary"
                  type="text"
                  onClick={handleRemoveSeriesFilter}
                />
              </div>
            </FlexContainer>
          )}
          {isCourseLanguagesFilterActive && (
            <FlexContainer width="auto">
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">{t('generic.lang')}</span>
                <MultiSelect
                  value={selectedCourseLanguages}
                  onChange={(e) => {
                    handleCourseLanguagesChange(e.value || []);
                    e.stopPropagation();
                  }}
                  options={courseLanguagesOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={t('generic.lang')}
                  maxSelectedLabels={0}
                  selectedItemsLabel={t('generic.itemsSelected.count')}
                  {...preventDropdownBrowserTranslate}
                />
                <AppButton
                  className="group-button"
                  icon="pi pi-times"
                  severity="secondary"
                  type="text"
                  onClick={handleRemoveCourseLanguagesFilter}
                />
              </div>
            </FlexContainer>
          )}
        </FlexContainer>
      )}
    </FlexContainer>
  );
};
